import { useEffect, useRef } from 'react';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { requestNotificationPermission } from '../utils/permission';

/**
 * Extract sound name from notification data
 * Backend always sends sound in data.sound field
 * Fallback to 'post' if not provided
 */
function extractSoundName(data = {}) {
  const soundName = data.sound && typeof data.sound === 'string' && data.sound.trim().length > 0 ? data.sound.trim() : 'post';

  return soundName;
}

export function usePushNotifications() {
  const initialized = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || initialized.current) return;
    initialized.current = true;

    const platform = Capacitor.getPlatform();

    const initPush = async () => {
      try {
        // Get device info for notification registration
        const storedScholarId = localStorage.getItem('scholar_id');
        const mobileDeviceInfo = {
          scholar_id: storedScholarId || 'unknown',
          platform,
          timestamp: new Date().toISOString(),
        };

        // Request notification permissions and register device
        await requestNotificationPermission(mobileDeviceInfo);

        // Request local notification permissions for foreground notifications
        const localPerm = await LocalNotifications.requestPermissions();

        if (localPerm.display !== 'granted') {
          console.warn('⚠️ Local notifications permission not granted');

          return;
        }

        // Create notification channel for Android
        if (platform === 'android') {
          await LocalNotifications.createChannel({
            id: 'classpep_default',
            name: 'Default',
            description: 'Default notification channel',
            importance: 5,
            visibility: 1,
            sound: 'default',
            vibration: true,
          });
        }

        // Listen for Firebase messages when app is in foreground (Android only)
        if (platform === 'android') {
          await FirebaseMessaging.addListener(
            'notificationReceived',
            async (event) => {
              const data = event.notification?.data || event.data || {};
              const title = event.notification?.title || event.title || data.title || 'Custom Notification';
              const body = event.notification?.body || event.body || data.body || '';
              const soundName = extractSoundName(data);
              const notificationId = Math.floor(Math.random() * 100000);
              const notificationConfig = {
                title,
                body,
                id: notificationId,
                schedule: { at: new Date(Date.now() + 500) },
                sound: `${soundName}.mp3`,
                data,
              };

              await LocalNotifications.schedule({
                notifications: [notificationConfig],
              });
            },
          );
        } else if (platform === 'ios') {
          // iOS: Listen for native push event and handle in JS if needed
          const handler = (_event) => {
            // Handle navigation, state, etc. here if needed
            // Example: console.log('🔵 JS received push:', event.detail);
          };

          window.addEventListener('pushNotificationReceived', handler);

          // Clean up
          return () => {
            window.removeEventListener('pushNotificationReceived', handler);
          };
        }

        // Listen for notification tap (both platforms)
        await FirebaseMessaging.addListener(
          'notificationActionPerformed',
          (event) => {
            window.dispatchEvent(
              new CustomEvent('notificationTapped', { detail: event }),
            );
          },
        );
      } catch (error) {
        console.error('❌ Push notification initialization error:', error);
      }
    };

    initPush();
  }, []);
}
